import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Filter, MousePointerClick, Eye, CheckCircle2, Inbox } from 'lucide-react';

interface FunnelRow {
  package_id: string;
  package_label: string;
  audience: string | null;
  agency_tier: string | null;
  city_section: string | null;
  inquiries: number;
  contacted: number;
  qualified: number;
  approved: number;
  active_subscriptions: number;
  active_banners: number;
  total_impressions: number;
  total_clicks: number;
}

const ALL = '__all__';

export function PackageFunnelAnalytics() {
  const [pkgId, setPkgId] = useState(ALL);
  const [city, setCity] = useState(ALL);
  const [tier, setTier] = useState(ALL);
  const [audience, setAudience] = useState(ALL);

  const { data: rows, isLoading } = useQuery({
    queryKey: ['pkg-funnel'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('package_funnel_metrics' as any)
        .select('*')
        .order('inquiries', { ascending: false });
      if (error) throw error;
      return (data as unknown as FunnelRow[]) || [];
    },
  });

  const cities = useMemo(() => Array.from(new Set((rows || []).map((r) => r.city_section).filter(Boolean))) as string[], [rows]);
  const tiers = useMemo(() => Array.from(new Set((rows || []).map((r) => r.agency_tier).filter(Boolean))) as string[], [rows]);
  const audiences = useMemo(() => Array.from(new Set((rows || []).map((r) => r.audience).filter(Boolean))) as string[], [rows]);
  const packages = useMemo(() => {
    const map = new Map<string, string>();
    (rows || []).forEach((r) => map.set(r.package_id, r.package_label));
    return Array.from(map.entries());
  }, [rows]);

  const filtered = (rows || []).filter((r) =>
    (pkgId === ALL || r.package_id === pkgId) &&
    (city === ALL || r.city_section === city) &&
    (tier === ALL || r.agency_tier === tier) &&
    (audience === ALL || r.audience === audience)
  );

  const totals = filtered.reduce((acc, r) => ({
    inquiries: acc.inquiries + Number(r.inquiries || 0),
    approved: acc.approved + Number(r.approved || 0),
    activeSubs: acc.activeSubs + Number(r.active_subscriptions || 0),
    activeBanners: acc.activeBanners + Number(r.active_banners || 0),
    impressions: acc.impressions + Number(r.total_impressions || 0),
    clicks: acc.clicks + Number(r.total_clicks || 0),
  }), { inquiries: 0, approved: 0, activeSubs: 0, activeBanners: 0, impressions: 0, clicks: 0 });

  const approvalRate = totals.inquiries ? Math.round((totals.approved / totals.inquiries) * 100) : 0;
  const activationRate = totals.approved ? Math.round((totals.activeBanners / totals.approved) * 100) : 0;
  const ctr = totals.impressions ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : '0.00';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" /> Package Conversion Funnel</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={pkgId} onValueChange={setPkgId}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Package" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All packages</SelectItem>
              {packages.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={audience} onValueChange={setAudience}>
            <SelectTrigger className="w-[150px]"><SelectValue placeholder="Audience" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All audiences</SelectItem>
              {audiences.map((a) => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="City section" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All city sections</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Agency tier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All tiers</SelectItem>
              {tiers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Stat icon={<Inbox className="h-4 w-4" />} label="Inquiries" value={totals.inquiries} />
          <Stat icon={<CheckCircle2 className="h-4 w-4" />} label="Approved" value={totals.approved} sub={`${approvalRate}% approval`} />
          <Stat icon={<CheckCircle2 className="h-4 w-4" />} label="Active subs" value={totals.activeSubs} />
          <Stat icon={<TrendingUp className="h-4 w-4" />} label="Active banners" value={totals.activeBanners} sub={`${activationRate}% activation`} />
          <Stat icon={<Eye className="h-4 w-4" />} label="Impressions" value={totals.impressions} />
          <Stat icon={<MousePointerClick className="h-4 w-4" />} label="Clicks" value={totals.clicks} sub={`${ctr}% CTR`} />
        </div>

        {isLoading ? <Skeleton className="h-48 w-full" /> : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No funnel data for these filters yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Package</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead className="text-right">Inquiries</TableHead>
                  <TableHead className="text-right">Contacted</TableHead>
                  <TableHead className="text-right">Qualified</TableHead>
                  <TableHead className="text-right">Approved</TableHead>
                  <TableHead className="text-right">Active subs</TableHead>
                  <TableHead className="text-right">Active banners</TableHead>
                  <TableHead className="text-right">Impr.</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r, idx) => (
                  <TableRow key={`${r.package_id}-${r.city_section}-${idx}`}>
                    <TableCell className="font-medium">{r.package_label}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{r.audience || '—'}</Badge></TableCell>
                    <TableCell className="text-xs">{r.city_section || 'all'}</TableCell>
                    <TableCell className="text-right">{r.inquiries}</TableCell>
                    <TableCell className="text-right">{r.contacted}</TableCell>
                    <TableCell className="text-right">{r.qualified}</TableCell>
                    <TableCell className="text-right font-medium text-primary">{r.approved}</TableCell>
                    <TableCell className="text-right">{r.active_subscriptions}</TableCell>
                    <TableCell className="text-right">{r.active_banners}</TableCell>
                    <TableCell className="text-right text-xs">{r.total_impressions}</TableCell>
                    <TableCell className="text-right text-xs">{r.total_clicks}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: number; sub?: string }) {
  return (
    <div className="border rounded-lg p-3 bg-card">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="text-xl font-semibold mt-1">{value.toLocaleString()}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
